<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceData',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onSaveNotes = (...args) => {
    console.log(props.invoice.notes);
  };

  const leftColumnLabelCols = ref(4);
  const ux = ref([]);

  const $data = computed(() => ({
    leftColumnLabelCols: leftColumnLabelCols.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'leftColumnLabelCols' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { leftColumnLabelCols, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    return false;
  });
  const invoiceTypeName = computed(() => {
    return props.invoice.invoice_type === 'correction'
      ? $fn.value.pgettext('InvoiceType', 'Correction')
      : props.invoice.invoice_type === 'storno'
        ? $fn.value.pgettext('InvoiceType', 'Storno')
        : props.invoice.invoice_type === 'proforma'
          ? $fn.value.pgettext('InvoiceType', 'Pro Forma')
          : '?';
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );

  const act_4337851679292638_emit_qtyzhv5zx_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4337851679292638_request_lxiex57ld_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_invoice_notes',
      data: { id: props.invoice.id, text: $event.data.value.text },
    };
  };
  const act_4152404563918957_emit_ragall67i_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3855552110905499_emit_88fgdect5_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4039445435535707_emit_td5cqmstv_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };

  const act_4337851679292638_emit_qtyzhv5zx = new actionTypes.EmitAction({
    actionArgs: act_4337851679292638_emit_qtyzhv5zx_getActionArgs,
  });
  const inst_evh_4337851679292638_success_on514vyvq =
    new actionTypes.EventHandlerImpl({
      action: act_4337851679292638_emit_qtyzhv5zx,
      event: 'success',
      displayName: 'emit',
    });
  const act_4337851679292638_request_lxiex57ld = new actionTypes.RequestAction({
    actionArgs: act_4337851679292638_request_lxiex57ld_getActionArgs,
    displayName: 'saveNotes',
    events: [inst_evh_4337851679292638_success_on514vyvq],
  });
  const inst_evh_4337851679292638_save_tc0wpuus1 =
    new actionTypes.EventHandlerImpl({
      action: act_4337851679292638_request_lxiex57ld,
      event: 'save',
      displayName: 'saveNotes',
    });
  const inst_evh_4337851679292638_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4337851679292638_save_tc0wpuus1],
    event: 'save',
  });
  const act_4152404563918957_emit_ragall67i = new actionTypes.EmitAction({
    actionArgs: act_4152404563918957_emit_ragall67i_getActionArgs,
  });
  const inst_evh_4152404563918957_reload_wdt7hr0x2 =
    new actionTypes.EventHandlerImpl({
      action: act_4152404563918957_emit_ragall67i,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_4152404563918957_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4152404563918957_reload_wdt7hr0x2],
    event: 'reload',
  });
  const act_3855552110905499_emit_88fgdect5 = new actionTypes.EmitAction({
    actionArgs: act_3855552110905499_emit_88fgdect5_getActionArgs,
  });
  const inst_evh_3855552110905499_reload_n7t1mg34a =
    new actionTypes.EventHandlerImpl({
      action: act_3855552110905499_emit_88fgdect5,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_3855552110905499_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3855552110905499_reload_n7t1mg34a],
    event: 'reload',
  });
  const act_4039445435535707_emit_td5cqmstv = new actionTypes.EmitAction({
    actionArgs: act_4039445435535707_emit_td5cqmstv_getActionArgs,
  });
  const inst_evh_4039445435535707_reload_jjgmhivht =
    new actionTypes.EventHandlerImpl({
      action: act_4039445435535707_emit_td5cqmstv,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_4039445435535707_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4039445435535707_reload_jjgmhivht],
    event: 'reload',
  });

  const actions = {
    inst_evh_4337851679292638_success_on514vyvq,
    inst_evh_4337851679292638_save_tc0wpuus1,
    saveNotes: inst_evh_4337851679292638_save_tc0wpuus1,
    inst_evh_4337851679292638_save,
    inst_evh_4152404563918957_reload_wdt7hr0x2,
    inst_evh_4152404563918957_reload,
    inst_evh_3855552110905499_reload_n7t1mg34a,
    inst_evh_3855552110905499_reload,
    inst_evh_4039445435535707_reload_jjgmhivht,
    inst_evh_4039445435535707_reload,
  };

  const evh_4337851679292638_success_on514vyvq = async (event, scope) => {
    return await actions.inst_evh_4337851679292638_success_on514vyvq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4337851679292638_save_tc0wpuus1 = async (event, scope) => {
    return await actions.inst_evh_4337851679292638_save_tc0wpuus1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4337851679292638_save = async (event, scope) => {
    return await actions.inst_evh_4337851679292638_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4152404563918957_reload_wdt7hr0x2 = async (event, scope) => {
    return await actions.inst_evh_4152404563918957_reload_wdt7hr0x2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4152404563918957_reload = async (event, scope) => {
    return await actions.inst_evh_4152404563918957_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3855552110905499_reload_n7t1mg34a = async (event, scope) => {
    return await actions.inst_evh_3855552110905499_reload_n7t1mg34a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3855552110905499_reload = async (event, scope) => {
    return await actions.inst_evh_3855552110905499_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4039445435535707_reload_jjgmhivht = async (event, scope) => {
    return await actions.inst_evh_4039445435535707_reload_jjgmhivht.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4039445435535707_reload = async (event, scope) => {
    return await actions.inst_evh_4039445435535707_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpRow>
    <ItpCol :w="5">
      <BListGroup flush class="list-group-alt-1">
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Own company')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
        >
          <UcCompanyMiniCard role="own" :item="invoice.company" no-icon>
          </UcCompanyMiniCard>
          <UcInvoicePartyPanel :item="invoice.company" v-if="invoice.company">
          </UcInvoicePartyPanel>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Partner')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
        >
          <UcCompanyMiniCard role="partner" :item="invoice.client" no-icon>
          </UcCompanyMiniCard>
          <UcInvoicePartyPanel :item="invoice.client" v-if="invoice.client">
          </UcInvoicePartyPanel>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Invoice date')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
          v-if="invoice.invoice_date"
        >
          <ItpText strong>
            {{ date(invoice.invoice_date) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Fulfillment date')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
          v-if="invoice.fulfillment_date"
        >
          <ItpText strong>
            {{ date(invoice.fulfillment_date) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Billing period')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
          v-if="
            invoice.billing_period &&
            (invoice.billing_period.start || invoice.billing_period.end)
          "
        >
          <div class="text-nowrap">
            <strong>
              {{ date(invoice.billing_period.start) }}
            </strong>
            <strong> - </strong>
            <strong>
              {{ date(invoice.billing_period.end) }}
            </strong>
          </div>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Accounting Date')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
          v-if="invoice.accounting_date"
        >
          <ItpText strong>
            {{ date(invoice.accounting_date) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'VAT Period')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
          v-if="invoice.vat_period && invoice.vat_period.name"
        >
          <ItpText strong>
            {{ invoice.vat_period.name }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Reference Number')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
          v-if="invoice.reference_number"
        >
          <ItpText strong>
            {{ invoice.reference_number }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Order Number')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
          v-if="invoice.order_number"
        >
          <ItpText strong>
            {{ invoice.order_number }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'File Number')"
          :labelCols="leftColumnLabelCols"
          class="mb-2"
          v-if="invoice.file_number"
        >
          <ItpText strong>
            {{ invoice.file_number }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <BListGroup flush class="list-group-alt-1 mb-2">
          <ItpLabeledListGroupItem
            :label="pgettext('Invoice', 'Notes')"
            :labelCols="leftColumnLabelCols"
          >
            <MultilineNotes
              :value="invoice.notes"
              :editable="!invoice.locked"
              @save="evh_4337851679292638_save($event, {})"
            >
            </MultilineNotes>
          </ItpLabeledListGroupItem>
        </BListGroup>
        <BListGroup
          flush
          class="list-group-alt-1"
          v-if="invoice.internal_notes"
        >
          <ItpLabeledListGroupItem
            :label="pgettext('Invoice', 'Internal Notes')"
            :labelCols="leftColumnLabelCols"
          >
            <ItpText :text="invoice.internal_notes" pre> </ItpText>
          </ItpLabeledListGroupItem>
        </BListGroup>
      </BListGroup>
    </ItpCol>
    <ItpCol :w="7">
      <BListGroup flush class="list-group-alt-1">
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Currency')"
          strong
          class="mb-2"
          v-if="invoice.currency"
        >
          <ItpText strong>
            {{ invoice.currency }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Exchange rate')"
          strong
          class="mb-2"
          v-if="invoice.exchange_rate && invoice.exchange_rate != 1"
        >
          <ItpText strong>
            {{ number(invoice.exchange_rate) }}
            <span v-if="invoice.exchange_rate_bank">
              &middot; {{ invoice.exchange_rate_bank }}
            </span>
            <span v-if="invoice.exchange_rate_date">
              ({{ date(invoice.exchange_rate_date) }})
            </span>
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Due date')"
          class="mb-2"
          v-if="invoice.due_date"
        >
          <ItpText strong>
            {{ date(invoice.due_date) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Payment Method')"
          class="mb-2"
          v-if="invoice.payment_method"
        >
          <ItpText strong>
            {{ pgettext('PaymentMethod', invoice.payment_method, 'Sentence') }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Expense Type')"
          class="mb-2"
          v-if="invoice.expense_type"
        >
          <ItpText strong>
            {{ pgettext('ExpenseType', invoice.expense_type, 'Sentence') }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Register entries')"
          class="mb-2"
          v-if="
            invoice.register_entries.data &&
            invoice.register_entries.data.length
          "
        >
          <UcRegisterEntryMiniCard
            :key="entry.id"
            :item="entry"
            no-icon
            v-for="entry in invoice.register_entries.data"
          >
          </UcRegisterEntryMiniCard>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'References')"
          class="mt-2 mb-2"
        >
          <UcInvoiceReferences
            :invoice="invoice"
            :reload="lastLoaded"
            @reload="evh_4152404563918957_reload($event, {})"
          >
          </UcInvoiceReferences>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Attachments')"
          class="mt-2 mb-2"
        >
          <UcAttachmentsPanel
            :parent_id="invoice.id"
            :parent="invoice"
            :readonly="invoice.isReadonly"
            :reload="lastLoaded"
            @reload="evh_3855552110905499_reload($event, {})"
          >
          </UcAttachmentsPanel>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Invoice', 'Payments')"
          class="mt-2 mb-2"
          v-if="invoice.totals"
        >
          <UcInvoicePayments
            :invoice="invoice"
            :reload="lastLoaded"
            @reload="evh_4039445435535707_reload($event, {})"
          >
          </UcInvoicePayments>
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpCol>
  </ItpRow>
</template>
